import { useQuery } from '@tanstack/react-query';

import { type ContentListItemType } from '@hivebrite/design-system/ContentList';
import { toast } from '@hivebrite/design-system/Toast';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import {
  RESULTS_LIMIT,
  MIN_SCORE_THRESHOLD,
} from 'hb-react/shared/components/ContentRecommendation/constants';
import { getRecommendedContents } from 'hb-react/shared/components/ContentRecommendation/requests';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import {
  LEGACY_ENTITY_TYPES,
  ENTITY_TYPES,
} from 'hb-react/shared/utils/entitiesContentData/constants';
import { transformDataList } from 'hb-react/shared/utils/entitiesContentData/transformToContentListItems';
import { type EntityType } from 'hb-react/shared/utils/entitiesContentData/types';

interface UseGetRecommendedContentsProps {
  contentId: number;
  contentType: EntityType;
  groupId?: number;
  recommendedContentTypes?: Array<EntityType>;
}

export const useGetRecommendedContents = ({
  contentId,
  contentType,
  groupId,
  recommendedContentTypes,
}: UseGetRecommendedContentsProps): {
  data: Array<ContentListItemType>;
  isLoading: boolean;
  refetch: () => void;
} => {
  const timeZoneConfiguration = useTimeZoneConfiguration();
  const { currentUser } = useDefaultAppContexts();
  const { whiteLabelURL } = useTopicContext();
  const flags = useFeatureFlag();
  const {
    flagUjAiContentRecommendationForumEvents,
    flagUjAiContentRecommendationAllTypes,
  } = flags || {};

  const getContentTypes = (): Array<EntityType> => {
    // If specific types are provided, use them
    if (recommendedContentTypes) {
      return recommendedContentTypes;
    }

    // If all types flag is enabled, return an empty array (backend determines types)
    if (flagUjAiContentRecommendationAllTypes) {
      return [];
    }

    // If forum events flag is enabled, include forum discussions, user events, and admin events
    if (flagUjAiContentRecommendationForumEvents) {
      return [
        ENTITY_TYPES.forumDiscussion,
        ENTITY_TYPES.news,
        ENTITY_TYPES.userEvent,
        LEGACY_ENTITY_TYPES.adminEvent,
      ];
    }

    // Default: only recommend News
    return [ENTITY_TYPES.news];
  };

  const contentTypes = getContentTypes();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['contentRecommendations', contentType, contentId],
    queryFn: async ({ signal }) => {
      try {
        const {
          data: { data: recommendedContents },
        } = await getRecommendedContents({
          params: {
            contentId,
            contentType,
            contentTypes,
            limit: RESULTS_LIMIT,
            scoreThreshold: MIN_SCORE_THRESHOLD,
            ...(groupId && { groupId }),
          },
          signal,
        });

        const transformedData = transformDataList(recommendedContents, {
          flags,
          isPublicOffice: !currentUser,
          isWhiteLabelGroup: !!whiteLabelURL,
          timeZoneConfiguration,
          withLink: true,
        });

        return transformedData.filter(Boolean);
      } catch (error) {
        toast.error(__('Sorry, an error occurred'));
        throw error;
      }
    },
    enabled: !!contentType,
    retry: false,
  });

  return {
    data: data || [],
    isLoading,
    refetch,
  };
};
